import type { SchoolPreset, WhiteLabelConfig } from './types';

// 12brain brand palette — teal accent family + blue ambient (ref: 12brain.com)
const basePalette: [string, string, string, string] = ['#2dd4bf', '#14b8a6', '#5eead4', '#155dfc'];

const baseTerminology = {
  learnerSingular: 'student',
  learnerPlural: 'students',
  lessonSingular: 'lesson',
  lessonPlural: 'lessons',
  moduleSingular: 'module',
  modulePlural: 'modules',
  missionSingular: 'mission',
  missionPlural: 'missions',
};

export const VIBE_CODE_CONFIG: WhiteLabelConfig = {
  presetId: 'coding-academy',
  brandName: '12brain Academy',
  legalName: '12Brain Solutions LLC',
  logoEmoji: '🧠',
  tagline: 'Learn AI & Vibe Coding with a live voice tutor',
  valueProposition: 'Master AI and vibe coding through guided video lessons with a real-time voice tutor that pauses to check your understanding.',
  subjectArea: 'AI & vibe coding',
  aiTutorName: 'Vibe',
  aiTutorRoleDescription: 'your 12brain Academy AI tutor',
  certificateProgramFallback: '12brain Academy Program',
  certificateIssuer: '12brain Academy',
  accentPalette: basePalette,
  terminology: baseTerminology,
};

export const DEFAULT_WHITE_LABEL_CONFIG: WhiteLabelConfig = {
  ...VIBE_CODE_CONFIG,
  presetId: 'custom',
  brandName: 'Minha Escola IA',
  legalName: 'Sua Organização',
  logoEmoji: '🏫',
  tagline: 'Uma escola adaptável para qualquer tipo de ensino',
  valueProposition: 'Crie sua própria escola digital com identidade visual, linguagem pedagógica e operação personalizada.',
  aiTutorName: 'Tutor IA',
  aiTutorRoleDescription: 'tutor especialista da escola',
  certificateProgramFallback: 'Programa Principal',
  certificateIssuer: 'Coordenação Acadêmica',
};

export const SCHOOL_PRESETS: SchoolPreset[] = [
  {
    id: 'coding-academy',
    name: 'Academia de Tecnologia',
    description: 'Formato clássico para cursos de tecnologia e programação.',
    config: VIBE_CODE_CONFIG,
  },
  {
    id: 'language-school',
    name: 'Escola de Idiomas',
    description: 'Vocabulário e discurso adaptados para aprendizagem de idiomas.',
    config: {
      ...DEFAULT_WHITE_LABEL_CONFIG,
      presetId: 'language-school',
      brandName: 'Fluent Path',
      legalName: 'Fluent Path Education',
      logoEmoji: '🌍',
      tagline: 'Fale com confiança em qualquer idioma',
      valueProposition: 'Desenvolva fluência com aulas práticas, tutor de IA e atividades interativas em contexto real.',
      subjectArea: 'idiomas',
      aiTutorName: 'Coach de Conversação IA',
      aiTutorRoleDescription: 'coach de idiomas da escola',
      certificateProgramFallback: 'Trilha de Fluência',
      terminology: {
        learnerSingular: 'estudante',
        learnerPlural: 'estudantes',
        lessonSingular: 'lição',
        lessonPlural: 'lições',
        moduleSingular: 'unidade',
        modulePlural: 'unidades',
        missionSingular: 'desafio',
        missionPlural: 'desafios',
      },
      accentPalette: ['#4f46e5', '#f97316', '#14b8a6', '#eab308'],
    },
  },
  {
    id: 'corporate-training',
    name: 'Treinamento Corporativo',
    description: 'Estrutura ideal para onboarding, compliance e capacitação interna.',
    config: {
      ...DEFAULT_WHITE_LABEL_CONFIG,
      presetId: 'corporate-training',
      brandName: 'SkillOps Academy',
      legalName: 'SkillOps Learning',
      logoEmoji: '🏢',
      tagline: 'Capacitação contínua para alta performance',
      valueProposition: 'Treine equipes com trilhas práticas, indicadores de desempenho e acompanhamento com IA.',
      subjectArea: 'competências profissionais',
      aiTutorName: 'Coach de Performance IA',
      aiTutorRoleDescription: 'mentor de treinamento corporativo',
      certificateProgramFallback: 'Trilha de Capacitação',
      terminology: {
        learnerSingular: 'colaborador',
        learnerPlural: 'colaboradores',
        lessonSingular: 'treinamento',
        lessonPlural: 'treinamentos',
        moduleSingular: 'trilha',
        modulePlural: 'trilhas',
        missionSingular: 'atividade',
        missionPlural: 'atividades',
      },
      accentPalette: ['#0f172a', '#dc2626', '#0891b2', '#16a34a'],
    },
  },
  {
    id: 'music-school',
    name: 'Escola de Música',
    description: 'Tom de comunicação e nomenclatura para ensino musical.',
    config: {
      ...DEFAULT_WHITE_LABEL_CONFIG,
      presetId: 'music-school',
      brandName: 'Studio Harmonia',
      legalName: 'Studio Harmonia Educação Musical',
      logoEmoji: '🎵',
      tagline: 'Desenvolva técnica, ouvido e expressão',
      valueProposition: 'Aprenda com repertório guiado, feedback de IA e exercícios práticos para evolução constante.',
      subjectArea: 'música',
      aiTutorName: 'Mentor Musical IA',
      aiTutorRoleDescription: 'mentor musical da escola',
      certificateProgramFallback: 'Programa de Performance',
      terminology: {
        learnerSingular: 'músico',
        learnerPlural: 'músicos',
        lessonSingular: 'aula',
        lessonPlural: 'aulas',
        moduleSingular: 'módulo',
        modulePlural: 'módulos',
        missionSingular: 'prática',
        missionPlural: 'práticas',
      },
      accentPalette: ['#7c3aed', '#db2777', '#f59e0b', '#22c55e'],
    },
  },
  {
    id: 'k12-school',
    name: 'Escola K-12',
    description: 'Linguagem mais ampla para múltiplas disciplinas escolares.',
    config: {
      ...DEFAULT_WHITE_LABEL_CONFIG,
      presetId: 'k12-school',
      brandName: 'NovaMente School',
      legalName: 'NovaMente Educação',
      logoEmoji: '📘',
      tagline: 'Aprender com curiosidade e consistência',
      valueProposition: 'Uma escola digital adaptável para diferentes disciplinas, séries e estilos de aprendizagem.',
      subjectArea: 'disciplinas escolares',
      aiTutorName: 'Tutor Pedagógico IA',
      aiTutorRoleDescription: 'tutor pedagógico da escola',
      certificateProgramFallback: 'Programa Educacional',
      terminology: {
        learnerSingular: 'estudante',
        learnerPlural: 'estudantes',
        lessonSingular: 'aula',
        lessonPlural: 'aulas',
        moduleSingular: 'unidade',
        modulePlural: 'unidades',
        missionSingular: 'atividade',
        missionPlural: 'atividades',
      },
      accentPalette: ['#1d4ed8', '#f97316', '#eab308', '#16a34a'],
    },
  },
];

export const PRESET_BY_ID = Object.fromEntries(
  SCHOOL_PRESETS.map((preset) => [preset.id, preset]),
) as Record<SchoolPreset['id'], SchoolPreset>;
